import { Question, QuestionTypeEnum } from '@/models/Question';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type QuestionSession = {
  questions: Question[];
  activeIndex: number;
  addQuestion: (type: QuestionTypeEnum) => void;
  replaceQuestion: (index: number, newType: QuestionTypeEnum) => void;
  removeQuestion: (index: number) => void;
  setActive: (index: number) => void;
};

export type QuestionSessionProps = {
  initialQuestions: Question[];
  isSaving: boolean;
  activityId: number;
  initialQuestion?: Question;
};

const calcNextActiveIndex = (
  activeIndex: number,
  deletedIndex: number,
  newLength: number,
) => {
  if (newLength === 0) return -1;
  if (deletedIndex < activeIndex) return activeIndex - 1;
  if (deletedIndex === activeIndex) return Math.min(activeIndex, newLength - 1);
  return activeIndex;
};

const mergeQuestions = (local: Question[], server: Question[]): Question[] => {
  const savedMap = new Map(server.map(q => [q.identifier, q]));
  const merged = local.map(q => savedMap.get(q.identifier) ?? q);
  const newFromServer = server.filter(
    q => !local.some(p => p.identifier === q.identifier),
  );
  const questions = [...merged, ...newFromServer].sort(
    (a, b) => a.order - b.order,
  );
  return questions;
};

const useQuestionSession = ({
  initialQuestions,
  isSaving,
  initialQuestion,
  activityId,
}: QuestionSessionProps): QuestionSession => {
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);
  const [activeIndex, setActiveIndex] = useState(() =>
    initialQuestions.findIndex(
      q => q.identifier === initialQuestion?.identifier,
    ),
  );

  useEffect(() => {
    if (isSaving) return;
    setQuestions(questions => mergeQuestions(questions, initialQuestions));
  }, [initialQuestions, isSaving]);

  const addQuestion = useCallback(
    (type: QuestionTypeEnum) => {
      setQuestions(prev => {
        const newQuestion: Question = {
          identifier: uuidv4(),
          type,
          order: Math.max(...prev.map(q => q.order), 0) + 1,
          title: '',
          activity: activityId,
          id: 0,
        };
        setActiveIndex(prev.length);
        return [...prev, newQuestion];
      });
    },
    [activityId],
  );

  const replaceQuestion = useCallback(
    (index: number, newType: QuestionTypeEnum) => {
      setQuestions(prev => {
        const old = prev[index];
        if (!old) return prev;
        const replaced: Question = {
          identifier: uuidv4(),
          type: newType,
          order: old.order,
          title: old.title,
          context: old.context,
          activity: activityId,
          id: 0,
        };
        const next = [...prev];
        next[index] = replaced;
        return next;
      });
    },
    [activityId],
  );

  const removeQuestion = useCallback((index: number) => {
    setQuestions(prev => {
      if (index < 0 || index >= prev.length) return prev;
      const next = prev
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, order: i + 1 }));
      setActiveIndex(current =>
        calcNextActiveIndex(current, index, next.length),
      );
      return next;
    });
  }, []);

  const setActive = useCallback(
    (index: number) => {
      const activeIndex = index >= 0 && index < questions.length ? index : -1;
      setActiveIndex(activeIndex);
    },
    [questions.length],
  );

  return {
    questions,
    activeIndex,
    addQuestion,
    replaceQuestion,
    removeQuestion,
    setActive,
  };
};

export default useQuestionSession;
